"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

/** 四张工艺配图：public/image/new-version/molding/overmolding/process/ */
const IMAGE_BASE = "/image/new-version/molding/overmolding/process";

const DEFAULT_IMAGES = [
	`${IMAGE_BASE}/plastic-over-plastic.png`,
	`${IMAGE_BASE}/rubber-over-plastic.png`,
	`${IMAGE_BASE}/plastic-over-metal.png`,
	`${IMAGE_BASE}/rubber-over-metal.png`,
] as const;

/** 每张卡对应的翻译 key 前缀（与 overmoldingProcessSection 中键名一致） */
const CARD_KEYS = [
	{
		prefix: "plasticPlastic",
		imageAltKey: "imageAltPlasticPlastic",
	},
	{
		prefix: "rubberPlastic",
		imageAltKey: "imageAltRubberPlastic",
	},
	{
		prefix: "plasticMetal",
		imageAltKey: "imageAltPlasticMetal",
	},
	{
		prefix: "rubberMetal",
		imageAltKey: "imageAltRubberMetal",
	},
] as const;

/** 绿色圆底白勾，与 PrototypeToolingAdvantages 一致 */
function CheckBullet() {
	return (
		<div
			className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-themePrimary400 sm:h-6 sm:w-6"
			aria-hidden
		>
			<svg className="h-3 w-3 text-white sm:h-3.5 sm:w-3.5" viewBox="0 0 12 10" fill="none" aria-hidden>
				<path
					d="M1 5l3.5 3.5L11 1"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</div>
	);
}

export interface OvermoldingProcessProps {
	/** 命名空间需含 title、各卡 Title/Desc/Item1～3、imageAlt* */
	translationNamespace?: string;
	/** 覆盖默认四图路径（顺序与 CARD_KEYS 一致） */
	imageUrls?: readonly string[];
}

/**
 * Overmolding Process：居中标题 + 右侧绿条，四列卡片（上图浅灰底 + 下文 + 勾选列表）；小屏 1 列、中屏 2 列、大屏 4 列
 */
export default function OvermoldingProcess({
	translationNamespace = "overmoldingProcessSection",
	imageUrls,
}: OvermoldingProcessProps) {
	const t = useTranslations(translationNamespace);
	const urls = imageUrls?.length === 4 ? imageUrls : DEFAULT_IMAGES;

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题与绿条：细线、加长，与标题文字底边对齐（items-end） */}
				<div className="mb-6 flex flex-wrap items-end justify-center gap-3 sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<div
						className="h-px w-20 shrink-0 rounded-full bg-themePrimary400 sm:h-[2px] sm:w-36 md:w-44"
						aria-hidden
					/>
				</div>

				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-5 xl:gap-6">
					{CARD_KEYS.map((card, index) => {
						const p = card.prefix;
						const itemKeys = [`${p}Item1`, `${p}Item2`, `${p}Item3`] as const;
						return (
							<article
								key={card.prefix}
								className="flex flex-col overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm transition-shadow hover:shadow-md"
							>
								<div className="flex min-h-[200px] items-center justify-center bg-[#f0f0f0] p-4 sm:min-h-[220px] md:p-5">
									<div className="relative aspect-[4/3] w-full max-w-[280px]">
										<Image
											src={urls[index] ?? urls[0]}
											alt={t(card.imageAltKey)}
											fill
											className="object-contain"
											sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
										/>
									</div>
								</div>
								<div className="flex flex-1 flex-col border-t border-gray-100 p-5 md:p-6">
									<h3 className="mb-3 text-left text-lg font-bold text-black sm:text-xl md:text-2xl">
										{t(`${p}Title`)}
									</h3>
									<p className="mb-4 text-left text-sm leading-relaxed text-black md:text-base">
										{t(`${p}Desc`)}
									</p>
									<ul className="mt-auto space-y-2.5">
										{itemKeys.map((key) => (
											<li key={key} className="flex items-start gap-2.5">
												<CheckBullet />
												<span className="text-left text-sm leading-relaxed text-black md:text-base">
													{t(key)}
												</span>
											</li>
										))}
									</ul>
								</div>
							</article>
						);
					})}
				</div>
			</div>
		</section>
	);
}
